import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldAlert, EyeOff, Eye, Trash2, Check, Flag } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function Moderation() {
  const { user } = useAuth();
  const [data, setData] = useState({ reports: [], open_count: 0, hidden_count: 0 });
  const [loading, setLoading] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/community/admin/reports");
      setData(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { if (user?.role === "owner") load(); }, [user, load]);

  if (user && user.role !== "owner") return <Navigate to="/home" replace />;

  const hidePost = async (postId, hidden) => {
    await api.post(`/community/admin/posts/${postId}/hide`, { hidden });
    toast.success(hidden ? "Post hidden from the circle." : "Post restored.");
    load();
  };
  const deletePost = (postId) => setPendingDelete(postId);
  const confirmDelete = async () => {
    const postId = pendingDelete;
    setPendingDelete(null);
    await api.delete(`/community/admin/posts/${postId}`);
    toast.success("Post deleted.");
    load();
  };
  const resolve = async (reportId) => {
    await api.post(`/community/admin/reports/${reportId}/resolve`);
    toast.success("Report dismissed.");
    load();
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="moderation-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Owner tools</p>
        <h1 className="font-heading text-3xl lg:text-4xl font-semibold mt-1">Moderation</h1>
        <p className="text-[#59535B] mt-2">Review reported posts and act in one tap to keep the sisterhood safe.</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-2xl p-5 card-soft" data-testid="mod-open-count">
          <p className="text-xs uppercase tracking-widest text-[#291B2D]/50">Open reports</p>
          <p className="font-heading text-3xl mt-1">{data.open_count}</p>
        </div>
        <div className="bg-white rounded-2xl p-5 card-soft" data-testid="mod-hidden-count">
          <p className="text-xs uppercase tracking-widest text-[#291B2D]/50">Hidden posts</p>
          <p className="font-heading text-3xl mt-1">{data.hidden_count}</p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-[#8A838E]">Loading…</p>
      ) : data.reports.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 text-center bg-white rounded-3xl border border-[#291B2D]/5" data-testid="moderation-empty">
          <span className="w-14 h-14 rounded-full bg-[#ECF3EE] text-[#3D6B52] flex items-center justify-center mb-3"><ShieldAlert className="h-6 w-6" /></span>
          <p className="font-heading text-lg">Nothing to review</p>
          <p className="text-sm text-[#8A838E] mt-1 max-w-sm">The community is calm. New reports will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {data.reports.map((r, i) => {
            const c = r.content;
            const isPost = r.target_type === "post";
            return (
              <motion.div key={r.report_id} data-testid={`mod-report-${r.report_id}`}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.03 }}
                className="bg-white border border-[#291B2D]/5 rounded-2xl p-5 card-soft">
                <div className="flex items-center gap-2 text-xs text-[#9E3A3A] font-semibold mb-2">
                  <Flag className="h-3.5 w-3.5" /> Reported {r.target_type}
                  {isPost && c?.report_count > 0 && <span className="text-[#8A838E] font-normal">· {c.report_count} report{c.report_count === 1 ? "" : "s"}</span>}
                  {isPost && c?.is_hidden && <span className="ml-1 px-2 py-0.5 rounded-full bg-[#F3EEF4] text-[#7B4C74]">Hidden</span>}
                </div>
                {c ? (
                  <>
                    <p className="text-sm text-[#59535B]">{c.author_name}</p>
                    <p className="text-[#252326] leading-relaxed whitespace-pre-wrap mt-1">{c.body}</p>
                    {c.image && <img src={c.image} alt="reported attachment" className="mt-3 rounded-xl max-h-56 w-auto" />}
                  </>
                ) : (
                  <p className="text-sm text-[#8A838E] italic">Content already removed.</p>
                )}
                {r.reason && <p className="text-xs text-[#8A838E] mt-2">Reason: {r.reason}</p>}

                <div className="flex flex-wrap items-center gap-2 mt-4">
                  {isPost && c && (
                    <button data-testid={`mod-hide-${r.report_id}`} onClick={() => hidePost(r.target_id, !c.is_hidden)}
                      className="inline-flex items-center gap-1.5 text-sm rounded-full px-3 py-1.5 border border-[#291B2D]/10 hover:bg-[#F7F2EA] transition-smooth">
                      {c.is_hidden ? <><Eye className="h-3.5 w-3.5" /> Unhide</> : <><EyeOff className="h-3.5 w-3.5" /> Hide</>}
                    </button>
                  )}
                  {isPost && (
                    <button data-testid={`mod-delete-${r.report_id}`} onClick={() => deletePost(r.target_id)}
                      className="inline-flex items-center gap-1.5 text-sm rounded-full px-3 py-1.5 border border-[#9E3A3A]/20 text-[#9E3A3A] hover:bg-[#FAF0F0] transition-smooth">
                      <Trash2 className="h-3.5 w-3.5" /> Delete
                    </button>
                  )}
                  <button data-testid={`mod-dismiss-${r.report_id}`} onClick={() => resolve(r.report_id)}
                    className="inline-flex items-center gap-1.5 text-sm rounded-full px-3 py-1.5 border border-[#3D6B52]/25 text-[#3D6B52] hover:bg-[#ECF3EE] transition-smooth">
                    <Check className="h-3.5 w-3.5" /> Dismiss
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="mod-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this post?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the post and all of its replies from the circle. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mod-delete-cancel">Keep it</AlertDialogCancel>
            <AlertDialogAction data-testid="mod-delete-confirm" onClick={confirmDelete}
              className="bg-[#9E3A3A] hover:bg-[#872F2F]">Delete post</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
